export type Preset = 'today' | '7' | '30' | '90' | 'month' | 'custom';

export const PRESETS: { value: Exclude<Preset, 'custom'>; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'month', label: 'This month' },
];

export interface Range {
  from: string;
  to: string;
}

/** A local calendar day as YYYY-MM-DD. */
export function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export function rangeFor(preset: Preset, custom: Range, now: Date = new Date()): Range {
  const today = isoDay(now);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case '7':
      return { from: isoDay(addDays(now, -6)), to: today };
    case '30':
      return { from: isoDay(addDays(now, -29)), to: today };
    case '90':
      return { from: isoDay(addDays(now, -89)), to: today };
    case 'month':
      return { from: isoDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    default:
      return custom.to < custom.from ? { from: custom.from, to: custom.from } : custom;
  }
}

/** The period of the same length that ends the day before this one starts. */
export function previousRange(r: Range): Range {
  const from = new Date(`${r.from}T00:00:00`);
  const to = new Date(`${r.to}T00:00:00`);
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  return { from: isoDay(addDays(from, -days)), to: isoDay(addDays(from, -1)) };
}

export function rangeText(r: Range): string {
  const f = (s: string, year: boolean) =>
    new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      ...(year ? { year: 'numeric' } : {}),
    });
  return r.from === r.to ? f(r.from, true) : `${f(r.from, false)} – ${f(r.to, true)}`;
}

/** "2 h 15 m", or "1.5 days" once it is long. */
export function durationText(hours: number | null): string {
  if (hours === null) return '—';
  if (hours >= 48) return `${(hours / 24).toFixed(1)} days`;
  if (hours >= 1) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m ? `${h} h ${m} m` : `${h} h`;
  }
  return `${Math.max(1, Math.round(hours * 60))} min`;
}

/** Shrink a long daily series to at most `points` sums, for a sparkline. */
export function bucket(values: number[], points = 12): number[] {
  if (values.length <= points) return values;
  const size = values.length / points;
  return Array.from({ length: points }, (_, i) =>
    values.slice(Math.round(i * size), Math.round((i + 1) * size)).reduce((a, b) => a + b, 0),
  );
}

/** Change against the previous period as a percentage, or null when there is nothing to compare. */
export function changePct(now: number, before: number): number | null {
  if (!(before > 0)) return null;
  return Math.round(((now - before) / before) * 100);
}
